/**
 * Shrinks a chosen photo to a 256x256 centre square JPEG before it is sent
 * (radaccess takes <= 64 KB, 32-1024 px). Browser only: canvas + Image.
 */
export const AVATAR_SIDE = 256;

export function squareCrop(w: number, h: number): { sx: number; sy: number; side: number } {
  const side = Math.min(w, h);
  return { sx: Math.floor((w - side) / 2), sy: Math.floor((h - side) / 2), side };
}

export async function shrinkPhoto(file: File): Promise<string> {
  if (!/^image\//.test(file.type)) throw new Error("not-an-image");
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = () => reject(new Error("not-an-image"));
      i.src = url;
    });
    const { sx, sy, side } = squareCrop(img.naturalWidth, img.naturalHeight);
    if (side < 32) throw new Error("not-an-image");
    const out = Math.min(AVATAR_SIDE, side);
    const canvas = document.createElement("canvas");
    canvas.width = out;
    canvas.height = out;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("not-an-image");
    ctx.fillStyle = "#ffffff"; // a transparent PNG becomes white, not black, as JPEG
    ctx.fillRect(0, 0, out, out);
    ctx.drawImage(img, sx, sy, side, side, 0, 0, out, out);
    return canvas.toDataURL("image/jpeg", 0.85);
  } finally {
    URL.revokeObjectURL(url);
  }
}
